import type { ContentAPIResponse } from '~/types/content'

/** Whatever `navigationStore.pageDataByKey()` returned for this route. */
type CmsPageData = ContentAPIResponse | false | undefined | null

/**
 * Registers the SEO head for a CMS-driven page from its Content API record.
 *
 * Three pages need this and each had grown its own copy. The two that were
 * ported straight from nuxt2 (`pages/help/faqs.vue`,
 * `pages/community/find-a-demonstrator.vue`) carried nuxt2's `useNuxt2Meta`
 * argument shape -- `{ title, meta: [{ vmid, name, content }, ...] }` -- into
 * `useSeoMeta`, which takes flat keys and knows nothing about a `meta` array.
 * Every value was silently dropped, so those two pages shipped with no title,
 * description or keywords at all.
 *
 * Two things matter here and both are easy to get wrong on their own, which is
 * the reason this is one function rather than a documented snippet:
 *
 * 1. **Getters, not a resolved snapshot.** The page data arrives from
 *    `useLazyAsyncData`, so at setup time `pageData` is still empty. Passing
 *    `useSeoMeta(metaInfo.value)` freezes the head at that first (empty) render
 *    and it never updates -- neither when the fetch resolves nor on a
 *    client-side navigation between two CMS routes. Reactive getters re-evaluate.
 * 2. **Keywords and `head` cannot go through `useSeoMeta`.** Unhead omits the
 *    deprecated `keywords` field from its typed input, and the CMS `head` blob is
 *    a nuxt2-era `<meta name="other">` tag with no typed equivalent. Both go
 *    through `useHead`'s raw meta array instead.
 *
 * Kept as a composable (not a component) because the three call sites render
 * completely different bodies -- only the head is shared.
 */
export const useCmsPageMeta = (pageData: () => CmsPageData) => {
  const metaInfo = computed(() => {
    const data = pageData()
    if (!data) {
      return {}
    }
    return {
      title: data.title,
      description: data.description,
      keywords: data.keywords,
      head: data.head,
    }
  })

  useSeoMeta({
    title: () => metaInfo.value.title,
    description: () => metaInfo.value.description,
  })

  // `other` is the tag name nuxt2 used for the CMS-authored head blob; kept
  // verbatim so CMS records written against it keep working.
  // Returns nothing on purpose: this registers a head and that is all it does. It used to
  // hand `metaInfo` back, which no call site read — an unused return invites the next
  // caller to assume it is load-bearing. AB#63280
  useHead(() => ({
    meta: [
      { name: 'keywords', content: metaInfo.value.keywords },
      { name: 'other', content: metaInfo.value.head },
    ],
  }))
}
